import { SearchOutlined, UserOutlined } from '@ant-design/icons';
import { Avatar, Col, Input, List, Modal, Space, Spin, Typography, message } from 'antd';
import { FormikProvider, useFormik } from 'formik';
import { useDebounce } from 'hooks/useDebounce';
import { ProjectType, ProjectUsers } from 'models/Project';
import { Users } from 'models/Users';
import { FC, Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useGetUsersQuery } from 'redux/services/cookieMonster/cookieMonsterApi';
import { useUpdateProjectMutation } from 'redux/services/mrsGrouch/mrsGrouchApi';
import * as yup from 'yup';

interface Props {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  // Edit mode inputs
  projectId?: string;
  currentProject?: ProjectType;
  // Create mode inputs
  mode?: 'edit' | 'create';
  onUserAdded?: (user: ProjectUsers) => void;
  existingUsers?: ProjectUsers[];
}

const userPayload = yup.object({
  fullName: yup.string().label('Full Name').required(),
  userId: yup.string().label('User ID').required()
});

export const AddUserModal: FC<Props> = ({ isOpen, setIsOpen, projectId, currentProject, mode = 'edit', onUserAdded, existingUsers = [] }) => {
  const [updateProject] = useUpdateProjectMutation();
  const [showUserList, setShowUserList] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [pageNumber, setPageNumber] = useState(0);
  const [allUsers, setAllUsers] = useState<Users[]>([]);
  const { divisionId } = useParams<{ divisionId: string }>();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // Debounce search text for API calls
  const debouncedSearchText = useDebounce(searchText, 900);

  // Only search if we have at least 3 characters
  const shouldSearch = debouncedSearchText.length >= 3;

  // Make two separate API calls when searching
  const nameQuery = useGetUsersQuery(
    {
      pageNumber,
      take: 20,
      divisionIds: divisionId,
      nameContains: shouldSearch ? debouncedSearchText : undefined
    },
    { skip: !isOpen || !shouldSearch }
  );

  const emailQuery = useGetUsersQuery(
    {
      pageNumber,
      take: 20,
      divisionIds: divisionId,
      emailContains: shouldSearch ? debouncedSearchText : undefined
    },
    { skip: !isOpen || !shouldSearch }
  );

  const allUsersQuery = useGetUsersQuery(
    {
      pageNumber,
      take: 20,
      divisionIds: divisionId
    },
    { skip: !isOpen }
  );

  // Merge results when searching, otherwise use all users
  const usersData = useMemo(() => {
    if (!shouldSearch) return allUsersQuery.data;

    if (!nameQuery.data && !emailQuery.data) return undefined;

    // Merge name and email results, removing duplicates
    const nameUsers = nameQuery.data?.data || [];
    const emailUsers = emailQuery.data?.data || [];

    const uniqueUsersMap = new Map<string, Users>();

    // Add name matches first
    nameUsers.forEach((user) => {
      uniqueUsersMap.set(user.user_id, user);
    });

    // Add email matches (this will overwrite if same user matches both)
    emailUsers.forEach((user) => {
      uniqueUsersMap.set(user.user_id, user);
    });

    const mergedUsers = Array.from(uniqueUsersMap.values());

    // Calculate total count (approximation)
    const totalCount = Math.max(nameQuery.data?.totalCount || 0, emailQuery.data?.totalCount || 0);

    return {
      data: mergedUsers,
      totalCount,
      pageNumber,
      take: 20
    };
  }, [shouldSearch, nameQuery.data, emailQuery.data, allUsersQuery.data, pageNumber]);

  const isLoading = shouldSearch ? nameQuery.isLoading || emailQuery.isLoading : allUsersQuery.isLoading;
  const isFetching = shouldSearch ? nameQuery.isFetching || emailQuery.isFetching : allUsersQuery.isFetching;

  // Update allUsers when new data comes in
  useEffect(() => {
    if (usersData?.data) {
      if (pageNumber === 0) {
        setAllUsers(usersData.data);
      } else {
        // Only add new users that aren't already in the list
        setAllUsers((prev) => {
          const existingIds = new Set(prev.map((u) => u.user_id));
          const newUsers = usersData.data.filter((u) => !existingIds.has(u.user_id));

          return [...prev, ...newUsers];
        });
      }
    }
  }, [usersData?.data, pageNumber, shouldSearch]);

  // Reset pagination when modal opens
  useEffect(() => {
    if (isOpen) {
      setPageNumber(0);
      setAllUsers([]);
    }
  }, [isOpen]);

  // Reset pagination when search changes
  useEffect(() => {
    setPageNumber(0);
    setAllUsers([]);
  }, [debouncedSearchText]);

  const loadMoreUsers = useCallback((): void => {
    if (!isLoading && !isFetching) {
      setPageNumber((prev) => prev + 1);
    }
  }, [isLoading, isFetching]);

  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>): void => {
      const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
      const scrollPercentage = (scrollTop + clientHeight) / scrollHeight;

      if (scrollPercentage > 0.8 && !isLoading && !isFetching && usersData?.totalCount && allUsers.length < usersData.totalCount) {
        loadMoreUsers();
      }
    },
    [loadMoreUsers, isLoading, isFetching, usersData?.totalCount, allUsers.length]
  );

  const addFormik = useFormik<ProjectUsers>({
    enableReinitialize: true,
    validationSchema: userPayload,
    initialValues: {
      fullName: '',
      userId: ''
    },
    onSubmit: async (values): Promise<void> => {
      // Duplicate checks
      const inEditProject = mode === 'edit' && currentProject?.users?.some((u) => u.userId === values.userId);
      const inCreateList = mode === 'create' && existingUsers?.some((u) => u.userId === values.userId);

      if (inEditProject || inCreateList) {
        message.error('User already exists in this project');

        return;
      }

      try {
        if (mode === 'edit' && currentProject && projectId) {
          const payload = {
            customerProjectId: currentProject.customerProjectId,
            owner: currentProject.owner,
            isActive: true,
            projectType: currentProject.projectType,
            status: currentProject.status,
            users: [...(currentProject.users || []), { ...values }]
          };

          await updateProject({
            divisionId: divisionId as string,
            projectId,
            payload
          }).unwrap();
        } else if (mode === 'create' && onUserAdded) {
          onUserAdded(values);
        }

        message.success('User added successfully');
        addFormik.resetForm();
        setIsOpen(false);
      } catch (error) {
        console.error('Error adding user:', error);
        message.error('Failed to add user. Please try again.');
      }
    }
  });

  const handleCancel = useCallback((): void => {
    setIsOpen(false);
    addFormik.resetForm();
    setShowUserList(false);
    setSearchText('');
    setPageNumber(0);
    setAllUsers([]);
  }, [addFormik, setIsOpen]);

  const handleUserSelect = useCallback(
    (user: Users): void => {
      addFormik.setValues({
        fullName: user.name,
        userId: user.user_id
      });
      setShowUserList(false);
      setSearchText(user.name);
    },
    [addFormik]
  );

  const filteredUsers = useMemo(
    () =>
      allUsers.filter((user): boolean => {
        const alreadyInProject = currentProject?.users?.some((projectUser) => projectUser.userId === user.user_id);

        return !alreadyInProject;
      }),
    [allUsers, currentProject?.users]
  );

  // Auto-load more users when we haven't reached the total count yet
  useEffect(() => {
    if (showUserList && !isLoading && !isFetching && usersData?.totalCount && allUsers.length < usersData.totalCount) {
      loadMoreUsers();
    }
  }, [showUserList, isLoading, isFetching, usersData?.totalCount, allUsers.length, loadMoreUsers]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowUserList(false);
      }
    };

    if (showUserList) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showUserList]);

  // Reset form errors when modal closes
  useEffect(() => {
    if (!isOpen) {
      addFormik.setTouched({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleSubmit = useCallback((): Promise<void> => addFormik.submitForm(), [addFormik]);

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>): void => setSearchText(e.target.value), []);

  const handleSearchFocus = useCallback((): void => setShowUserList(true), []);

  return (
    <Fragment>
      <Modal destroyOnClose okText="Submit" onOk={handleSubmit} onCancel={handleCancel} open={isOpen}>
        <FormikProvider value={addFormik}>
          <Col span={24} style={{ background: 'rgb(255, 234, 193)', borderRadius: 10, marginBottom: 10, marginTop: 20 }}>
            <Typography.Title level={4} style={{ fontWeight: 400, fontSize: 20, marginBottom: 0, padding: 10, marginLeft: 10 }}>
              Add New User
            </Typography.Title>
          </Col>
          <Col style={{ margin: '20px 10px' }}>
            <Space style={{ width: '100%' }} direction="vertical" size={15}>
              <div style={{ position: 'relative' }} ref={dropdownRef}>
                <Input
                  placeholder="Search by name or email (min. 3 characters)..."
                  value={searchText}
                  onChange={handleSearchChange}
                  onFocus={handleSearchFocus}
                  prefix={<SearchOutlined />}
                  style={{ width: '100%' }}
                />
                {searchText.length > 0 && searchText.length < 3 && <div style={{ fontSize: '12px', color: '#999', marginTop: '4px' }}>Type at least 3 characters to search</div>}
                {showUserList && (
                  <div
                    ref={listRef}
                    style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      backgroundColor: 'white',
                      border: '1px solid #d9d9d9',
                      borderRadius: '6px',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                      zIndex: 1000,
                      maxHeight: '300px',
                      overflowY: 'auto'
                    }}
                    onScroll={handleScroll}>
                    <Spin spinning={isLoading && pageNumber === 0}>
                      {debouncedSearchText !== searchText && (
                        <div style={{ padding: '12px', textAlign: 'center', color: '#999' }}>
                          <Spin size="small" />
                          <span style={{ marginLeft: 8 }}>Searching...</span>
                        </div>
                      )}
                      {filteredUsers.length > 0 ? (
                        <>
                          <List
                            dataSource={filteredUsers}
                            renderItem={(user: Users): React.ReactNode => (
                              <List.Item style={{ padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid #f0f0f0' }} onClick={(): void => handleUserSelect(user)}>
                                <List.Item.Meta avatar={<Avatar icon={<UserOutlined />} style={{ backgroundColor: '#1890ff' }} />} title={user.name} description={user.email} />
                              </List.Item>
                            )}
                          />
                          {!isLoading && !isFetching && usersData?.data && usersData.data.length !== undefined && usersData.data.length < 20 && (
                            <div style={{ padding: '8px 12px', textAlign: 'center', color: '#999', fontSize: '12px', borderTop: '1px solid #f0f0f0' }}>✨ You&apos;ve reached all available users</div>
                          )}
                        </>
                      ) : (
                        <div style={{ padding: '12px', textAlign: 'center', color: '#999' }}>{searchText ? 'No users found' : 'No users available'}</div>
                      )}
                      {(isLoading || isFetching) && pageNumber > 0 && (
                        <div style={{ padding: '12px', textAlign: 'center' }}>
                          <Spin size="small" />
                          <span style={{ marginLeft: 8, color: '#999' }}>Loading more users... (Page {pageNumber + 1})</span>
                        </div>
                      )}
                    </Spin>
                  </div>
                )}
              </div>
            </Space>
          </Col>
        </FormikProvider>
      </Modal>
    </Fragment>
  );
};
